import React, { useState } from 'react';
import Modal from '../../../components/Modal';
import { Button } from '../../../components';
import { localTime } from '../../../utils/datetime';
import { LEAVE_TYPES, leaveTypeByKey } from '../../../utils/leaveTypes';

// Resolve one review item (mirrors the duty-roster ReviewModal).
//   • differs → approve the recorded actual time, or correct it before approval
//   • missed  → record forgotten clock times, OR mark the carer absent
// Both resolutions stage a draft change (synced on Publish).
// The absence recorded here is leave like any other, so it offers the same
// three types with the same words — see utils/leaveTypes.js.
const ABSENCE_OPTS = LEAVE_TYPES;

const VisitReviewModal = ({ item, onClose, onRecordHours, onMarkAway }) => {
  const v = item?.visit;
  const actIn = v?.actual_start ? localTime(v.actual_start) : '';
  const actOut = v?.actual_end ? localTime(v.actual_end) : '';
  const [clockIn, setClockIn] = useState(item?.kind === 'differs' ? (actIn || v?.start || '') : '');
  const [clockOut, setClockOut] = useState(item?.kind === 'differs' ? (actOut || v?.end || '') : '');
  const [note, setNote] = useState('');
  const [absenceType, setAbsenceType] = useState('sick');

  if (!item) return null;
  const isMissed = item.kind === 'missed';
  const who = `${item.carer.name} · ${v.resident?.name || v.package_name} · ${item.zone.name}`;

  // One approve button (same as the care-home ReviewModal): it approves what is
  // in the fields, and its label says whether that is the recorded time or an
  // edit. A field that no longer matches the recorded time is tagged "Edited".
  const inEdited = clockIn !== actIn;
  const outEdited = clockOut !== actOut;
  const isEdited = inEdited || outEdited;
  const editedBadge = <span className="absolute -top-2 right-2 rounded bg-blue-600 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white">Edited</span>;
  const fieldCls = (edited) => `w-full rounded-lg border bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 py-2 text-[14px] outline-none focus:border-blue-500 ${edited ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-[#E2E8F0] dark:border-gray-700'}`;

  return (
    <Modal
      isOpen={!!item}
      onClose={onClose}
      title={isMissed ? 'Review missed visit' : 'Review clocking difference'}
      maxWidth={isMissed ? 'max-w-[760px]' : 'max-w-[640px]'}
    >
      <p className="-mt-1 text-[13px] text-[#64748B] dark:text-gray-400">{who} · Scheduled {v.start}{v.end ? `–${v.end}` : ''}</p>

      {isMissed ? (
        <div className="mt-4 space-y-5">
          {/* Forgotten clocking — manual hours */}
          <div className="rounded-2xl border border-blue-100 bg-blue-50/40 p-4">
            <h4 className="text-[13px] font-bold text-[#1E3A8A]">Forgotten clocking?</h4>
            <p className="mb-3 mt-0.5 text-[11px] text-[#64748B] dark:text-gray-400">Add the actual hours worked. The record shows a manager entered and approved them.</p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[150px] flex-1">
                <label className="mb-1 block text-[11px] font-extrabold uppercase tracking-wide text-[#475569] dark:text-gray-400">Actual clock-in</label>
                <input type="time" value={clockIn} onChange={(e) => setClockIn(e.target.value)}
                  className="w-full rounded-lg border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 py-2 text-[14px] outline-none focus:border-blue-500" />
              </div>
              <div className="min-w-[150px] flex-1">
                <label className="mb-1 block text-[11px] font-extrabold uppercase tracking-wide text-[#475569] dark:text-gray-400">Actual clock-out</label>
                <input type="time" value={clockOut} onChange={(e) => setClockOut(e.target.value)}
                  className="w-full rounded-lg border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 py-2 text-[14px] outline-none focus:border-blue-500" />
              </div>
              <Button variant="primary" onClick={() => onRecordHours(clockIn, clockOut, note)}>
                Add &amp; approve hours
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-3 text-[10px] font-extrabold uppercase tracking-wider text-[#8796aa]">
            <span className="h-px flex-1 bg-[#E2E8F0]" />OR MARK AN ABSENCE<span className="h-px flex-1 bg-[#E2E8F0]" />
          </div>

          <div className="grid grid-cols-3 gap-2.5">
            {ABSENCE_OPTS.map((o) => (
              <button key={o.key} onClick={() => setAbsenceType(o.key)}
                className={`rounded-xl border bg-white dark:bg-[#31373f] p-2.5 text-left transition ${absenceType === o.key ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-[#E2E8F0] dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}>
                <div className={`mb-2 h-1 rounded-full ${o.bar}`} />
                <div className="text-[12px] font-bold text-[#1E293B] dark:text-gray-200">{o.label}</div>
                <div className="mt-0.5 text-[9px] text-[#7d8da3]">{o.sub}</div>
              </button>
            ))}
          </div>

          <div>
            <label className="mb-1.5 block text-[12px] font-extrabold uppercase tracking-wide text-[#475569] dark:text-gray-400">Manager note</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note for the audit trail…"
              className="w-full resize-none rounded-lg border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 py-2 text-[13px] outline-none focus:border-blue-500" />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={() => onMarkAway(absenceType, note, leaveTypeByKey(absenceType)?.label)}>
              Mark absence
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-[#E2E8F0] dark:border-gray-800 bg-[#FBFCFE] dark:bg-[#20242c] p-3.5">
              <div className="mb-1 text-[10px] font-extrabold uppercase tracking-wide text-[#8796aa]">Scheduled</div>
              <div className="text-[18px] font-black text-[#344258] dark:text-gray-200">{v.start} – {v.end || '—'}</div>
            </div>
            <div className="rounded-xl border border-[#E2E8F0] dark:border-gray-800 bg-[#FBFCFE] dark:bg-[#20242c] p-3.5">
              <div className="mb-1 text-[10px] font-extrabold uppercase tracking-wide text-[#8796aa]">Actual clocked</div>
              <div className="text-[18px] font-black text-[#344258] dark:text-gray-200">{actIn || '—'} – {actOut || 'running…'}</div>
            </div>
          </div>
          <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/10 px-3.5 py-2.5 text-[12px] leading-relaxed text-amber-800 dark:text-amber-400">
            Approve the recorded times or correct them below.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-[11px] font-extrabold uppercase tracking-wide text-[#475569] dark:text-gray-400">Actual clock-in</label>
              <div className="relative">
                <input type="time" value={clockIn} onChange={(e) => setClockIn(e.target.value)} className={fieldCls(inEdited)} />
                {inEdited && editedBadge}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-extrabold uppercase tracking-wide text-[#475569] dark:text-gray-400">Actual clock-out</label>
              <div className="relative">
                <input type="time" value={clockOut} onChange={(e) => setClockOut(e.target.value)} className={fieldCls(outEdited)} />
                {outEdited && editedBadge}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => onRecordHours(clockIn, clockOut, isEdited ? 'Edited & approved' : 'Approved recorded time')}
              disabled={!clockIn || !clockOut}
            >
              {isEdited ? 'Approve edited times' : 'Approve actual times'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default VisitReviewModal;
